import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Check } from "lucide-react";
import { services } from "../data/services";
import { useSEO } from "../lib/seo";
import { FadeUp } from "../components/motion";
import { useLang } from "../i18n";
import { track } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const industriesOpt = ["Real Estate", "Construction", "Entertainment", "Leisure", "Other"];
const budgetsByLang = {
  en: ["Under $50k", "$50k – $150k", "$150k – $500k", "$500k+", "Not sure yet"],
  az: ["50.000 AZN-dən az", "50.000 – 150.000 AZN", "150.000 – 500.000 AZN", "500.000+ AZN", "Hələ dəqiq deyil"],
};

const inputCls =
  "w-full rounded-xl border border-white/15 bg-surface px-4 py-3.5 text-base text-white placeholder:text-white/35 outline-none transition-colors duration-300 focus:border-gold";

const labelCls = "mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-white/60";

const steps = [{"n":"01","t":"Short brief","d":"Initial information about the business, objective and main constraint."},{"n":"02","t":"Strategic meeting","d":"A working conversation to clarify fit and the problem, rather than a sales presentation."},{"n":"03","t":"Tailored scope","d":"Priorities, stages, resources and measures of success."}];

export default function Contact() {
  const { t, lang } = useLang();
  const budgets = budgetsByLang[lang];
  useSEO(
    t("Contact — YUXEL"),
    t("Book a consultation with YUXEL. Tell us the ambition; we'll map the constraint and the plan.")
  );
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    industry: "",
    services: [],
    budget: "",
    message: "",
    website: "",
    phone: "",
    role: "",
    need: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (name) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(name)
        ? f.services.filter((s) => s !== name)
        : [...f.services, name],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, message: ["Role: " + form.role, "Phone / WhatsApp: " + form.phone, "Problem: " + form.need, "12-month ambition: " + form.message].join("\n\n") }),
      });
      if (!res.ok) throw new Error("failed");
      setSubmitted(true);
      track("lead_submit", { industry: form.industry, services: form.services.join(","), budget: form.budget });
      toast.success(t("Request received — we'll be in touch within two business days."));
    } catch {
      toast.error(t("Something went wrong. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="yx-contact-intro"><p className="yx-contact-kicker">YUXEL / {t("Contact")}</p><h1>{t("Let’s discuss the next stage of your business.")}</h1><p>{t("Share where your business is, where it wants to go and what stands in the way. If there is a fit, our first meeting will establish the direction for the next stage.")}</p></div>
      <section className="creative-contact-page" aria-label="Contact form">
        <div className="creative-contact-grid mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 py-24 sm:px-8 sm:py-32 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <FadeUp>
              <p className="lux-eyebrow text-ink/50">{t("How the first conversation works")}</p>
              <h2 className="contact-process-title mt-5">{t("Clear steps. No sales theatre.")}</h2>
            </FadeUp>
            <div className="mt-10 space-y-0 border-t border-ink/15">
            {steps.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.07}>
                <div data-testid={`contact-step-${s.n}`} className="flex gap-6 border-b border-white/10 py-7">
                  <span className="font-display text-2xl font-bold text-outline">{s.n}</span>
                  <div>
                    <h2 className="contact-step-heading font-display text-lg font-semibold text-white">{t(s.t)}</h2>
                    <p className="mt-1.5 text-sm leading-relaxed text-mist">{t(s.d)}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          {submitted ? (
            <FadeUp>
              <div
                data-testid="lead-form-success-panel"
                className="flex h-full min-h-[480px] flex-col items-center justify-center border border-gold/30 bg-panel p-10 text-center"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 text-gold">
                  <Check className="h-8 w-8" />
                </span>
                <h2 className="mt-8 font-serif text-3xl font-medium text-white sm:text-4xl">
                  {t("Brief received.")}
                </h2>
                <p className="mt-4 max-w-md text-base leading-relaxed text-mist">
                  {t("Your brief has been received. We will review it and contact you within two business days.")}
                </p>
              </div>
            </FadeUp>
          ) : (
            <FadeUp delay={0.1}>
              <form
                data-testid="lead-form"
                onSubmit={onSubmit}
                className="border border-white/10 bg-panel p-7 sm:p-10"
              >
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] h-0 w-0 opacity-0"
                />
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {[
                    {key:"name",label:"Full name *",type:"text",required:true,autoComplete:"name",max:120},
                    {key:"company",label:"Company *",type:"text",required:true,autoComplete:"organization",max:160},
                    {key:"role",label:"Role",type:"text",required:false,autoComplete:"organization-title",max:160},
                    {key:"phone",label:"Phone / WhatsApp *",type:"tel",required:true,autoComplete:"tel",max:160},
                    {key:"email",label:"Work email *",type:"email",required:true,autoComplete:"email",max:254},
                  ].map(field=><div key={field.key}>
                    <label htmlFor={'lf-'+field.key} className={labelCls}>{t(field.label)}</label>
                    <input id={'lf-'+field.key} data-testid={'lead-form-'+field.key+'-input'} type={field.type} required={field.required} autoComplete={field.autoComplete} minLength={field.key==='name'?2:undefined} maxLength={field.max} value={form[field.key]} onChange={e=>set(field.key,e.target.value)} className={inputCls}/>
                  </div>)}
                  <div><label htmlFor="lf-industry" className={labelCls}>{t("Industry")}</label>
                    <select id="lf-industry" data-testid="lead-form-industry-select" value={form.industry} onChange={e=>set("industry",e.target.value)} className={inputCls}>
                      <option value="">{t("Select industry")}</option>
                      {industriesOpt.map(o=><option key={o} value={o}>{t(o)}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-8"><label htmlFor="lf-need" className={labelCls}>{t("Main problem or need *")}</label><textarea id="lf-need" required maxLength={1600} rows={3} value={form.need} onChange={e=>set("need",e.target.value)} className={inputCls}/></div>
                <fieldset className="mt-8"><legend className={labelCls}>{t("What do you need built?")}</legend>
                  <div className="flex flex-wrap gap-2.5">{services.map(service=><button key={service.slug} type="button" data-testid={'lead-form-service-chip-'+service.slug} onClick={()=>toggleService(service.name)} aria-pressed={form.services.includes(service.name)} className={form.services.includes(service.name)?"rounded-full border border-gold bg-gold px-4 py-2 text-sm font-medium text-ink":"rounded-full border border-white/20 px-4 py-2 text-sm font-medium text-white/70 hover:border-gold"}>{t(service.name)}</button>)}</div>
                </fieldset>
                <div className="mt-8"><label htmlFor="lf-budget" className={labelCls}>{t("Planned project budget")}</label>
                  <select id="lf-budget" data-testid="lead-form-budget-select" value={form.budget} onChange={e=>set("budget",e.target.value)} className={inputCls}><option value="">{t("Select range")}</option>{budgets.map(b=><option key={b} value={b}>{b}</option>)}</select>
                </div>
                <div className="mt-8"><label htmlFor="lf-message" className={labelCls}>{t("Your main ambition for the next 12 months *")}</label><textarea id="lf-message" data-testid="lead-form-message-textarea" required minLength={10} maxLength={1600} rows={5} value={form.message} onChange={e=>set("message",e.target.value)} className={inputCls}/></div>

                <button
                  type="submit"
                  data-testid="lead-form-submit-button"
                  disabled={loading}
                  className="group mt-10 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 font-semibold text-ink transition-all duration-300 hover:bg-champagne  disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {loading ? t("Sending…") : t("Discuss the project")}
                  <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <p className="mt-5 text-xs leading-relaxed text-white/40">
                  {t("Your information is used only to assess collaboration fit and is not shared with third parties.")}
                </p>
              </form>
            </FadeUp>
          )}
          </div>
        </div>
      </section>
    </>
  );
}
